/**
 * Spot a failed network call made by a script the visitor's browser injected
 * into the page (an extension, adware), so beforeSend can drop it.
 *
 * TOPBROKERCRM-2M5, -2M6, -2M7 and -2M8 are one browser: an injected script
 * (`Object.postUserData`, eval'd, driven by a MutationObserver) posting to a
 * host we have never called, the request failing, and nothing in that script
 * catching the rejection. None of it is ours, and because the injected frames
 * carry a different line and column every time, each occurrence opened a new
 * issue.
 *
 * Neither list-based knob can express this:
 *  - `ignoreErrors: 'Failed to fetch'` would also silence a failed fetch() to
 *    our own API;
 *  - `denyUrls` looks at the last frame that has a URL, which here is either
 *    the page itself or our own bundle (the SDK's fetch wrapper sits on top of
 *    the stack).
 *
 * So the call is attributed by the request when the SDK recorded it, and by
 * the stack when it did not: a fetch issued before Sentry.init() wrapped
 * window.fetch leaves no breadcrumb and no wrapper frame (2M6, 2M7).
 *
 * Sentry only ever sees the calls that FAIL, so these events were never a way
 * to detect such a script. See docs/sentry-noise-filter.md.
 */

// Chrome / Safari / Firefox wording for a fetch() that got no response. Newer
// SDKs append the host: "Failed to fetch (example.com)".
const FETCH_FAILURE = /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)( \(.+\))?$/;

// The SDK records the failed request in the same tick the rejection is raised
// (1-2ms apart in the four issues). Anything older belongs to another call.
const RECENT_SECONDS = 5;

/**
 * A URL we cannot read counts as ours, so the event is kept.
 *
 * @param {string|undefined|null} url
 * @param {string} origin
 * @returns {boolean}
 */
function isOwnUrl(url, origin) {
    if (!url || typeof url !== 'string') {
        return true;
    }

    try {
        return new URL(url, origin).origin === origin;
    } catch (e) {
        return true;
    }
}

/**
 * The failed fetch the SDK recorded just before this event, if any.
 *
 * @param {any} event
 * @returns {any|null}
 */
function recentFailedFetch(event) {
    const breadcrumbs = Array.isArray(event.breadcrumbs) ? event.breadcrumbs : [];

    for (let i = breadcrumbs.length - 1; i >= 0; i--) {
        const breadcrumb = breadcrumbs[i];

        if (breadcrumb?.category === 'fetch' && breadcrumb.level === 'error') {
            return event.timestamp - breadcrumb.timestamp <= RECENT_SECONDS ? breadcrumb : null;
        }
    }

    return null;
}

/**
 * True when the event is a fetch() that failed in code that is not ours.
 *
 * @param {any} event   the Sentry event, as handed to beforeSend
 * @param {string} origin   our own origin, e.g. window.location.origin
 * @returns {boolean}
 */
export function isInjectedScriptFetchFailure(event, origin) {
    const exception = event?.exception?.values?.[0];

    if (!exception || !FETCH_FAILURE.test(exception.value || '')) {
        return false;
    }

    const failed = recentFailedFetch(event);

    if (failed) {
        return !isOwnUrl(failed.data?.url, origin);
    }

    // No breadcrumb: the call went out before the SDK wrapped window.fetch, so
    // the stack is all we have. Our own code always runs from the Vite bundle.
    const frames = exception.stacktrace?.frames || [];

    return frames.length > 0 && !frames.some((frame) => /\/build\//.test(frame?.filename || ''));
}
